import {
  AuthSocialAccountTypeLimited,
  InfluencersProfileFormat,
  MutationrequestEngagementGoogleDocsReportArgs,
  MutationrequestPackageProposalExcelArgs,
  MutationrequestPackageProposalPptxArgs,
  MutationrequestProposalPptxArgs,
  MutationrequestProposalSpreadsheetArgs,
  MutationrequestTrendingInfluencersSpreadsheetArgs,
  MutationrequestUserProposalExcelArgs,
  MutationrequestUserProposalPptxArgs,
  ProposalDownloadFormat,
  useRequestEngagementGoogleDocsReportMutation,
  useRequestPackageProposalExcelMutation,
  useRequestPackageProposalPptxMutation,
  useRequestProposalPptxMutation,
  useRequestProposalSpreadsheetMutation,
  useRequestTrendingInfluencersSpreadsheetMutation,
  useRequestUserProposalExcelMutation,
  useRequestUserProposalPptxMutation,
  useSocialAuthRedirectUrlLazyQuery
} from '@anytag/graphql';
import { CustomDownloadType, DownloadFileType, RedirectType } from '@anytag/shared-types';
import { getCallbackUrl } from '@anytag/shared-utils';
import { manageLocalStorage } from '@anytag/router';
import { useState } from 'react';
import { useQueryHelper } from '@anytag/shared-hooks';
import { useUserRoles } from '@anytag/auth';
import { GoogleAuthDownloadJsonType, GoogleAuthDownloadProps } from './types';
import { GoogleAuthDialogProps } from './google-auth-dialog';

export const useGoogleAuthDownload = (props: GoogleAuthDownloadProps) => {
  const { goBackUrl, searchParams } = props;

  const engagementId = 'engagementId' in props ? props.engagementId : undefined;
  const packageId = 'packageId' in props ? props.packageId : undefined;
  const influencerIds = 'influencerIds' in props ? props.influencerIds : undefined;

  const { t, navigate, enqueueSnackbar } = useQueryHelper();
  const { isAdminStaff, isPartner } = useUserRoles();
  const [authDownloadJson, setAuthDownloadJson] = useState<GoogleAuthDownloadJsonType | null>(null);
  const { callRequestProposalPptx, loading: downloadingPptx } = useRequestProposalPptxMutation();
  const { callRequestProposalSpreadsheet, loading: downloadingSpreadsheet } = useRequestProposalSpreadsheetMutation();
  const { getSocialAuthRedirectUrl } = useSocialAuthRedirectUrlLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const { callRequestUserProposalPptx } = useRequestUserProposalPptxMutation({ fetchPolicy: 'no-cache' });
  const { callRequestUserProposalExcel } = useRequestUserProposalExcelMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestPackageProposalExcel } = useRequestPackageProposalExcelMutation({ fetchPolicy: 'no-cache' });
  const { callRequestPackageProposalPptx } = useRequestPackageProposalPptxMutation({ fetchPolicy: 'no-cache' });
  const { callRequestEngagementGoogleDocsReport } = useRequestEngagementGoogleDocsReportMutation({
    fetchPolicy: 'no-cache'
  });
  const { callRequestTrendingInfluencersSpreadsheet } = useRequestTrendingInfluencersSpreadsheetMutation({
    fetchPolicy: 'no-cache'
  });

  const formattedVariables = {
    engagementId: (engagementId || undefined) as number,
    influencerIds: influencerIds?.length && influencerIds.length > 0 ? influencerIds : null
  };

  const updateAuthDownloadJson = ({ format, variables }: Pick<GoogleAuthDownloadJsonType, 'variables' | 'format'>) => {
    setAuthDownloadJson({
      format,
      variables,
      goBackUrl,
      searchParams,
      urlParams: {
        engagementId: engagementId?.toString(),
        packageId: packageId?.toString()
      }
    });
  };

  const handleDownloadProposalWithGoogleAuth = async () => {
    if (!authDownloadJson) return;

    const { data } = await getSocialAuthRedirectUrl({
      fetchPolicy: 'no-cache',
      variables: {
        input: {
          callbackUrl: getCallbackUrl(null, RedirectType.GOOGLE_AUTH),
          provider: AuthSocialAccountTypeLimited.GOOGLE
        }
      }
    });

    manageLocalStorage('set_json', 'google-auth-download-json', authDownloadJson);
    navigate({ href: data?.socialAuthRedirectUrl?.redirectUri });
  };

  const handleEngProposalGoogleSlideDownload = async () => {
    const variables: MutationrequestProposalPptxArgs = {
      format: DownloadFileType.GOOGLE_SLIDE,
      engagementId: formattedVariables.engagementId,
      influencerIds: formattedVariables.influencerIds
    };
    try {
      await callRequestProposalPptx({ variables });
      enqueueSnackbar(t('Google Slide Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.GOOGLE_SLIDE });
    }
  };

  const handleEngProposalSpreadsheetDownload = async () => {
    const variables: MutationrequestProposalSpreadsheetArgs = {
      engagementId: formattedVariables.engagementId,
      influencerIds: formattedVariables.influencerIds
    };
    try {
      await callRequestProposalSpreadsheet({ variables });
      enqueueSnackbar(t('Google Sheets Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.SPREADSHEET });
    }
  };

  const handleInfluencerProposalGoogleSlideDownload = async () => {
    const variables: MutationrequestUserProposalPptxArgs = {
      format: InfluencersProfileFormat.GOOGLE_SLIDE,
      influencerIds: formattedVariables.influencerIds
    };
    try {
      await callRequestUserProposalPptx({ variables });
      enqueueSnackbar(t('Google Slide Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.GOOGLE_SLIDE });
    }
  };

  const handleInfluencerProposalSpreadsheetDownload = async () => {
    const variables: MutationrequestUserProposalExcelArgs = {
      format: ProposalDownloadFormat.SPREADSHEET,
      influencerIds: formattedVariables.influencerIds
    };
    try {
      await callRequestUserProposalExcel({ variables });
      enqueueSnackbar(t('Google Sheets Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.SPREADSHEET });
    }
  };

  const handlePackageProposalGoogleSpreadsheetDownload = async () => {
    if (!packageId) return;

    const variables: MutationrequestPackageProposalExcelArgs = {
      packageId,
      format: ProposalDownloadFormat.SPREADSHEET,
      influencerIds: formattedVariables.influencerIds
    };

    try {
      await callRequestPackageProposalExcel({ variables });
      enqueueSnackbar(t('Google Slide Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.GOOGLE_SLIDE });
    }
  };

  const handlePackageProposalGoogleSlideDownload = async () => {
    if (!packageId) return;

    const variables: MutationrequestPackageProposalPptxArgs = {
      packageId,
      format: InfluencersProfileFormat.GOOGLE_SLIDE,
      influencerIds: formattedVariables.influencerIds
    };

    try {
      await callRequestPackageProposalPptx({ variables });
      enqueueSnackbar(t('Google Slide Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.GOOGLE_SLIDE });
    }
  };

  const handleEngagementGoogleDocsDownload = async (isVideoType: boolean) => {
    if (!engagementId) return;

    const variables: MutationrequestEngagementGoogleDocsReportArgs = {
      campaignId: engagementId,
      isVideoType
    };

    try {
      await callRequestEngagementGoogleDocsReport({ variables: { campaignId: engagementId, isVideoType } });
      enqueueSnackbar(t('Google Docs Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({
        variables,
        format: isVideoType ? CustomDownloadType.GOOGLE_DOCS_VIDEO : CustomDownloadType.GOOGLE_DOCS_STATIC_POST
      });
    }
  };

  const handleTrendingInfluencersSpreadsheetDownload = async (
    variables: MutationrequestTrendingInfluencersSpreadsheetArgs
  ) => {
    try {
      await callRequestTrendingInfluencersSpreadsheet({ variables });
      enqueueSnackbar(t('Google Sheets Report has been sent to your email and saved to the Google Drive'), {
        variant: 'success'
      });
    } catch (error) {
      updateAuthDownloadJson({ variables, format: DownloadFileType.SPREADSHEET });
    }
  };

  return {
    canDownloadGoogleReport: isAdminStaff || isPartner,
    downloadingPptx,
    downloadingSpreadsheet,
    handleDownloadProposalWithGoogleAuth,
    handleEngProposalGoogleSlideDownload,
    handleEngProposalSpreadsheetDownload,
    handleInfluencerProposalGoogleSlideDownload,
    handleInfluencerProposalSpreadsheetDownload,
    callRequestProposalPptx,
    callRequestProposalSpreadsheet,
    handlePackageProposalGoogleSpreadsheetDownload,
    handlePackageProposalGoogleSlideDownload,
    handleEngagementGoogleDocsDownload,
    handleTrendingInfluencersSpreadsheetDownload,
    dialogProps: {
      open: !!authDownloadJson,
      onClose: () => setAuthDownloadJson(null),
      authType: authDownloadJson?.format,
      onAuthenticate: handleDownloadProposalWithGoogleAuth
    } satisfies GoogleAuthDialogProps
  };
};
